import type { ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import { useT } from "../../i18n/locale";
import { SETTINGS_SECTIONS, settingsGroupsOf, settingsSectionsOf, type SettingsSectionId } from "../../routes/settings/sections";
import { useAccess, useMenuSubject } from "../../session/access";
import { PageHeader } from "./PageHeader";
import "./settings-layout.css";

// Intestazione e menu delle Impostazioni (piano M1b D15). Le sezioni, e chi le vede, stanno in `routes/settings/sections.ts`;
// il menu mostra quelle del menu della scheda (`useMenuSubject`): nella Vista Commerciale le sezioni del ruolo del
// Commerciale scelto (decisione del 2 ottobre 2026). Una pagina dentro una sezione (per esempio «Importazione guidata da
// Close») ha il suo titolo e il ritorno alla sezione; nel menu resta segnata la sezione. Il menu è a gruppi con un titolo
// piccolo (IMP-13): ogni gruppo è una lista col nome del gruppo, così anche chi legge con lo screen reader sa dove si trova; un
// gruppo senza sezioni per quella persona non c'è, titolo compreso. Sotto i 980 px il menu resta lo stesso, in colonna.

type Props = {
  section: SettingsSectionId;
  /** Il titolo di una pagina dentro la sezione; senza, il nome della sezione. */
  title?: string;
  /** Accanto al titolo, per esempio il ritorno alla sezione. */
  actions?: ReactNode;
  children: ReactNode;
};

/** Intestazione «Impostazioni · {azienda}» con il nome della sezione, navigazione delle sezioni e contenuto. */
export function SettingsLayout({ section, title, actions, children }: Props) {
  const t = useT();
  const { context } = useAccess();
  const groups = settingsGroupsOf(settingsSectionsOf(useMenuSubject()));
  const current = SETTINGS_SECTIONS.find((candidate) => candidate.id === section) ?? SETTINGS_SECTIONS[0];
  return (
    <>
      <PageHeader eyebrow={t("settings.eyebrow", { organization: context.organization.name })} title={title ?? (current ? t(current.label) : "")} actions={actions} />
      <div className="g-settings">
        <nav className="g-settings__nav" aria-label={t("settings.sections")}>
          {groups.map((group) => (
            <div key={group.id} className="g-settings__group">
              <p className="g-settings__group-title" id={`g-settings-group-${group.id}`}>
                {t(group.label)}
              </p>
              <ul aria-labelledby={`g-settings-group-${group.id}`}>
                {group.sections.map((candidate) => (
                  <li key={candidate.id}>
                    <Link to={candidate.to}>{t(candidate.label)}</Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>
        <div className="g-settings__body">{children}</div>
      </div>
    </>
  );
}
